import { useEffect, useState } from 'react';
import { onQueueChange, pendingCount, flushQueue } from '../lib/offline';
import { getToken } from '../lib/api';

/**
 * Connection and sync state.
 *
 * A rep needs to know, without thinking about it, whether what they just typed
 * is actually safe. Silence is the wrong answer when three updates are sitting
 * on the phone unsent.
 */
export default function SyncStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    pendingCount().then(setPending);
    const off = onQueueChange(setPending);
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      off();
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      await flushQueue(getToken);
      setPending(await pendingCount());
    } finally {
      setSyncing(false);
    }
  };

  if (online && pending === 0) return null;

  const label = !online
    ? (pending ? `Offline · ${pending} change${pending === 1 ? '' : 's'} saved here` : 'Offline — you can keep working')
    : `${pending} change${pending === 1 ? '' : 's'} waiting to sync`;

  return (
    <div className={`sync-bar ${online ? 'pending' : 'offline'}`} role="status" aria-live="polite">
      <span className="dot" aria-hidden="true" />
      <span className="label">{label}</span>
      {online && pending > 0 ? (
        <button className="sync-now" onClick={syncNow} disabled={syncing}>
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
      ) : null}
    </div>
  );
}
